// ── Banque de sons locale (localStorage) ──────────────────────────────────
//
// Avec R2 actif, ce cache local sert uniquement au re-upload automatique
// si le fichier n'est plus sur le serveur. Les erreurs de quota sont
// silencieuses — R2 est la source de vérité.
//
// Structure d'une entrée :
// {
//   filename: 'mid__Drake_instrumental.mp3',
//   label:    'Drake instrumental',
//   mood:     'mid',
//   duration: 187.4,
//   size:     3145728,
//   b64:      'data:audio/mpeg;base64,...'
// }

const LS_KEY = 'timbo_local_music_v1';
export const LOCAL_MUSIC_QUOTA = 8 * 1024 * 1024; // 8MB soft limit

// ── Lecture ────────────────────────────────────────────────────────────────
export function getLocalTracks() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

// ── Sauvegarde ────────────────────────────────────────────────────────────
function saveTracks(tracks) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(tracks)); return true; }
  catch { return false; }
}

// ── Taille totale utilisée (en bytes approximatif) ────────────────────────
export function localMusicUsedBytes() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? raw.length : 0;
  } catch { return 0; }
}

export function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Ajouter un track depuis un File ───────────────────────────────────────
// Retourne { ok: boolean, error?: string, track? }
// Avec R2 actif, les erreurs de quota sont silencieuses (ok: true malgré tout)
export async function addLocalTrack(file, { filename, label, mood, duration }) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const b64 = ev.target.result;
      const size = file.size;
      const tracks = getLocalTracks();

      // Soft quota check — on tente quand même
      const currentUsed = localMusicUsedBytes();
      if (currentUsed + b64.length > LOCAL_MUSIC_QUOTA * 1.5) {
        // R2 est la source de vérité — on résout ok:true silencieusement
        resolve({ ok: true, track: null });
        return;
      }

      const existing = tracks.findIndex(t => t.filename === filename);
      const entry = { filename, label, mood, duration, size, b64 };
      if (existing >= 0) tracks[existing] = entry;
      else tracks.push(entry);

      const saved = saveTracks(tracks);
      // Si localStorage plein, on résout quand même ok:true (R2 a le fichier)
      resolve({ ok: true, track: saved ? entry : null });
    };
    reader.onerror = () => resolve({ ok: false, error: 'Lecture fichier échouée' });
    reader.readAsDataURL(file);
  });
}

// ── Supprimer un track ────────────────────────────────────────────────────
export function removeLocalTrack(filename) {
  const tracks = getLocalTracks().filter(t => t.filename !== filename);
  saveTracks(tracks);
}

// ── Récupérer le b64 d'un track spécifique ────────────────────────────────
export function getLocalTrackB64(filename) {
  const t = getLocalTracks().find(t => t.filename === filename);
  return t ? t.b64 : null;
}

// ── Convertir b64 en File (pour re-upload Railway) ────────────────────────
export function b64ToFile(b64, filename) {
  try {
    const [header, data] = b64.split(',');
    const mime = header.match(/data:([^;]+)/)?.[1] || 'audio/mpeg';
    const binary = atob(data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], filename, { type: mime });
  } catch { return null; }
}
